// components/Pages/ContactPageRenderer.jsx
'use client'
import React, { useState } from 'react'
import Script from 'next/script'
import Image from 'next/image'
import LinkComponent from '@components/LinkComponent'
import SellFormRenderer        from '@components/Forms/SellForm'
import ContactFormRenderer     from '@components/Forms/ContactUsForm'
import RecruitmentFormRenderer from '@components/Forms/RecruitmentForm'
import { FA_ICON_MAP } from '@components/shared/faIconMap'
import { GRID_LAYOUTS, getGridSlotLetters } from '@components/shared/imageLayouts'
import { CONTACT_BTN_PRESETS, CONTACT_BTN_ICON_PATHS, CONTACT_BTN_ICON_TYPE } from '@components/shared/contactLayouts'

function RenderIcon({ icon, size = 20, color, style }) {
  if (!icon) return null
  const Icon = FA_ICON_MAP[icon]
  if (Icon) return <Icon size={size} color={color} style={style} />
  return <span style={{ fontSize: size, color, lineHeight: 1, ...style }}>{icon}</span>
}

// A url/href equal to this exact string means "open the Contact Us form
// popup" instead of navigating anywhere. Set from the editor via the
// "Open Contact Us form popup instead" shortcut on any URL field.
const CONTACT_FORM_TRIGGER = '#contact-form'
const isFormTrigger = (url) => url === CONTACT_FORM_TRIGGER

// Simple centered-overlay modal that hosts the Contact Us form. Closes on
// backdrop click or the × button.
function ContactFormModal({ onClose, website, lang }) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', overflow: 'hidden', boxSizing: 'border-box' }}
    >
      {/* This outer box never scrolls and never overflows its own bounds —
          only the form content inside it does. That's what keeps the close
          button fully visible instead of being clipped by a scrollbar. */}
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', width: '100%', maxWidth: 480, maxHeight: '90vh', boxSizing: 'border-box' }}>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, width: 32, height: 32, borderRadius: '50%', background: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, lineHeight: 1, color: '#374151' }}
        >
          ✕
        </button>
        <div style={{ maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', borderRadius: 8 }}>
          <ContactFormRenderer cardStyle={website?.contactForm} lang={lang} userId={website?.userId} />
        </div>
      </div>
    </div>
  )
}

// ── i18n helper ───────────────────────────────────────────────────────────────
const t = (obj, lang) => {
  if (!obj) return ''
  if (typeof obj === 'string') return obj
  return obj[lang] || obj['en'] || ''
}

// ── RTL helpers ───────────────────────────────────────────────────────────────
const ALIGN_MAP      = { left: 'start', right: 'end', center: 'center', start: 'start', end: 'end' }
const normalizeAlign = (align) => ALIGN_MAP[align] ?? 'start'
const toJustify      = (align) => align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start'
const toAlignItems   = (align) => align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start'

function ContactIcon({ preset, size = 16, color = 'currentColor' }) {
  const path = CONTACT_BTN_ICON_PATHS[preset]
  if (!path) return null
  if (CONTACT_BTN_ICON_TYPE[preset] === 'stroke') {
    const [d, poly] = path.split('|')
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={d} />{poly && <polyline points={poly} />}
      </svg>
    )
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d={path} /></svg>
}

// ── Banner ────────────────────────────────────────────────────────────────────
// No search bar (by design — this is the contact-page-only banner).
function BannerSection({ config, lang, onOpenContactForm }) {
  const isSplit = config.layout === 'split-right' || config.layout === 'split-left'
  const splitRight = config.layout === 'split-right'

  const bg = config.bgType === 'image' ? `url(${config.bgImageUrl}) center/cover no-repeat`
    : config.bgType === 'gradient' ? config.bgGradient
    : (config.bgColor ?? 'var(--color-main)')

  const titleHtml = config.useRichText ? t(config.titleHtml, lang) : null
  const subtitleHtml = config.useRichText ? t(config.subtitleHtml, lang) : null

  const renderButton = (prefix, key, defaults) => {
    const style       = config[`${prefix}Style`]       ?? defaults.style
    const color       = config[`${prefix}Color`]       ?? '#ffffff'
    const bgColor     = config[`${prefix}BgColor`]     ?? defaults.bg
    const borderColor = config[`${prefix}BorderColor`] ?? 'rgba(255,255,255,0.8)'
    const borderWidth = config[`${prefix}BorderWidth`] ?? 2
    const radius      = config[`${prefix}Radius`]      ?? 6
    const fontSize    = config[`${prefix}FontSize`]    ?? '1rem'
    const paddingX    = config[`${prefix}PaddingX`]    ?? '1.5rem'
    const paddingY    = config[`${prefix}PaddingY`]    ?? '0.625rem'
    const text        = t(config[`${prefix}Text`], lang) || defaults.text
    const url         = config[`${prefix}Url`] ?? '/'
    const btnStyle = {
      display: 'inline-block', fontSize, fontWeight: 600, lineHeight: 1.4, color,
      background: style === 'ghost' ? 'transparent' : bgColor,
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius: style === 'pill' ? '999px' : `${radius}px`,
      padding: `${paddingY} ${paddingX}`, textDecoration: 'none', whiteSpace: 'nowrap',
    }
    if (isFormTrigger(url)) {
      return <button key={key} type="button" onClick={onOpenContactForm} style={{ ...btnStyle, cursor: 'pointer', fontFamily: 'inherit' }}>{text}</button>
    }
    return (
      <LinkComponent key={key} href={url} style={btnStyle}>
        {text}
      </LinkComponent>
    )
  }

  const ctaButtons = (mt) => (config.showCta || config.showCta2) && (
    <div style={{
      display: 'flex', flexDirection: config.ctaLayout ?? 'row',
      alignItems: config.ctaAlign ?? 'center', justifyContent: config.ctaAlign ?? 'center',
      gap: config.ctaGap ?? '1rem', marginTop: mt, flexWrap: 'wrap',
    }}>
      <style>{`
        @media (max-width: 1023px) { .banner-cta-${config === config ? 'x' : 'x'} {} }
      `}</style>
      {config.showCta && renderButton('cta', 'p', { style: 'filled', bg: 'var(--color-main)', text: 'Get in Touch' })}
      {config.showCta2 && renderButton('cta2', 's', { style: 'outlined', bg: 'transparent', text: 'Learn More' })}
    </div>
  )

  if (isSplit) {
    return (
      <section style={{ background: config.bgColor ?? '#ffffff' }}>
        <style>{`
          .cb-split { display: flex; flex-direction: ${splitRight ? 'row' : 'row-reverse'}; min-height: 360px; }
          @media (max-width: 767px) { .cb-split { flex-direction: column; } }
        `}</style>
        <div className="cb-split">
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: `${config.paddingY ?? '3rem'} ${config.paddingX ?? '3rem'}`, textAlign: normalizeAlign(config.contentAlign ?? 'center') }}>
            <div style={{ width: '100%' }}>
              {titleHtml
                ? <div style={{ fontSize: config.titleSize ?? '3rem', fontWeight: config.titleWeight ?? 700, color: config.titleColor ?? '#1e293b', lineHeight: config.titleLineHeight ?? 1.15 }} dangerouslySetInnerHTML={{ __html: titleHtml }} />
                : <h1 style={{ fontSize: config.titleSize ?? '3rem', fontWeight: config.titleWeight ?? 700, color: config.titleColor ?? '#1e293b', lineHeight: config.titleLineHeight ?? 1.15, margin: 0 }}>{t(config.title, lang) || 'Contact Us'}</h1>}
              {subtitleHtml
                ? <div style={{ fontSize: config.subtitleSize ?? '1.125rem', color: config.subtitleColor ?? '#6b7280', marginTop: config.subtitleMt ?? '1rem' }} dangerouslySetInnerHTML={{ __html: subtitleHtml }} />
                : t(config.subtitle, lang) && <p style={{ fontSize: config.subtitleSize ?? '1.125rem', color: config.subtitleColor ?? '#6b7280', marginTop: config.subtitleMt ?? '1rem' }}>{t(config.subtitle, lang)}</p>}
              {ctaButtons(config.ctaMt ?? '2rem')}
            </div>
          </div>
          <div style={{ flex: 1, position: 'relative', minHeight: 260, aspectRatio: config.splitImageAspect !== 'auto' ? config.splitImageAspect : undefined, background: config.splitImageUrl ? `url(${config.splitImageUrl}) center/cover no-repeat` : '#e2e8f0' }} />
        </div>
      </section>
    )
  }

  return (
    <section className="cb-banner-classic" style={{ position: 'relative', minHeight: config.minHeight ?? '420px', background: bg, display: 'flex', alignItems: config.contentVAlign === 'top' ? 'flex-start' : config.contentVAlign === 'bottom' ? 'flex-end' : 'center', justifyContent: 'center', padding: `${config.paddingY ?? '5rem'} ${config.paddingX ?? '5rem'}`, overflow: 'hidden' }}>
      <style>{`@media (max-width: 767px) { .cb-banner-classic { min-height: ${config.minHeightMobile ?? '60dvh'} !important; } }`}</style>
      {config.bgType === 'image' && config.overlayOpacity > 0 && (
        <div style={{ position: 'absolute', inset: 0, background: config.overlayColor ?? '#000000', opacity: config.overlayOpacity }} />
      )}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: config.maxContentWidth ?? '700px', textAlign: normalizeAlign(config.contentAlign ?? 'center'), width: '100%' }}>
        {titleHtml
          ? <div style={{ fontSize: config.titleSize ?? '3rem', fontWeight: config.titleWeight ?? 700, color: config.titleColor ?? '#ffffff', lineHeight: config.titleLineHeight ?? 1.15 }} dangerouslySetInnerHTML={{ __html: titleHtml }} />
          : <h1 style={{ fontSize: config.titleSize ?? '3rem', fontWeight: config.titleWeight ?? 700, color: config.titleColor ?? '#ffffff', lineHeight: config.titleLineHeight ?? 1.15, margin: 0 }}>{t(config.title, lang) || 'Contact Us'}</h1>}
        {subtitleHtml
          ? <div style={{ fontSize: config.subtitleSize ?? '1.125rem', color: config.subtitleColor ?? 'rgba(255,255,255,0.85)', marginTop: config.subtitleMt ?? '1rem' }} dangerouslySetInnerHTML={{ __html: subtitleHtml }} />
          : t(config.subtitle, lang) && <p style={{ fontSize: config.subtitleSize ?? '1.125rem', color: config.subtitleColor ?? 'rgba(255,255,255,0.85)', marginTop: config.subtitleMt ?? '1rem' }}>{t(config.subtitle, lang)}</p>}
        {ctaButtons(config.ctaMt ?? '2rem')}
      </div>
    </section>
  )
}

// ── Contact Info / Channels ───────────────────────────────────────────────────
function ContactChannelCard({ ch, lang, row, cardStyle, cardBg, accent, cardRadius, onOpenContactForm }) {
  const label = t(ch.label, lang)
  const isExternal = /^(https?:|tel:|mailto:)/i.test(ch.url ?? '')
  const iconColor = cardStyle === 'filled' ? '#ffffff' : accent
  const wrapperStyle = {
    background:   cardStyle === 'filled' ? accent : cardStyle === 'bordered' ? cardBg : 'transparent',
    border:       cardStyle === 'bordered' ? `1px solid ${accent}` : 'none',
    borderRadius: cardRadius,
    padding:      row ? '0.875rem 1rem' : '1.25rem 1rem',
    display:      'flex',
    flexDirection: row ? 'row' : 'column',
    alignItems:   'center',
    gap:          row ? '0.75rem' : '0.5rem',
    textAlign:    row ? 'start' : 'center',
    textDecoration: 'none',
    height:       '100%',
    boxSizing:    'border-box',
  }
  const content = (
    <>
      <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 1 }}>
        {ch.type && ch.type !== 'custom' && CONTACT_BTN_ICON_PATHS[ch.type]
          ? <ContactIcon preset={ch.type} size={row ? 24 : 32} color={iconColor} />
          : <RenderIcon icon={ch.icon} size={row ? 24 : 32} color={iconColor} />}
      </span>
      <div>
        {label && <div style={{ fontWeight: 700, fontSize: '0.875rem', color: cardStyle === 'filled' ? '#ffffff' : '#374151' }}>{label}</div>}
        {ch.value && <div style={{ fontSize: '0.8125rem', color: cardStyle === 'filled' ? 'rgba(255,255,255,0.85)' : '#6b7280', marginTop: 2 }}>{ch.value}</div>}
      </div>
    </>
  )
  if (!ch.url) return <div style={wrapperStyle}>{content}</div>
  if (isFormTrigger(ch.url)) {
    return (
      <button type="button" onClick={onOpenContactForm} style={{ ...wrapperStyle, cursor: 'pointer', fontFamily: 'inherit', width: '100%', border: wrapperStyle.border === 'none' ? 'none' : wrapperStyle.border, appearance: 'none' }}>
        {content}
      </button>
    )
  }
  return (
    <a href={ch.url} style={wrapperStyle} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined}>
      {content}
    </a>
  )
}

function ContactInfoSection({ config, lang, onOpenContactForm }) {
  const channels = (config.channels ?? []).filter(c => c.show !== false)
  if (!channels.length) return null
  const layout      = config.layout ?? 'grid'
  const cols        = config.columns ?? 4
  const cardStyle   = config.cardStyle ?? 'bordered'
  const cardBg      = config.cardBg ?? '#f8fafc'
  const accent      = config.accentColor ?? 'var(--color-main)'
  const cardRadius  = config.cardRadius ?? '0.75rem'
  const titleAlign  = normalizeAlign(config.titleAlign ?? 'left')

  return (
    <section style={{ background: config.sectionBg ?? '#ffffff', padding: `${config.sectionPadding ?? '4rem'} 2rem` }}>
      <style>{`
        .ci-grid-${cols} { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
        @media (min-width: 640px)  { .ci-grid-${cols} { grid-template-columns: repeat(${Math.min(cols, 2)}, 1fr); } }
        @media (min-width: 1024px) { .ci-grid-${cols} { grid-template-columns: repeat(${cols}, 1fr); } }
      `}</style>
      <div className="container-wide mx-auto">
        {config.showTitle !== false && t(config.title, lang) && (
          <h2 style={{ fontSize: config.titleSize ?? '1.75rem', fontWeight: config.titleWeight ?? '700', color: config.titleColor ?? 'var(--color-main)', textAlign: titleAlign, marginBottom: '2rem' }}>
            {t(config.title, lang)}
          </h2>
        )}
        {layout === 'grid' ? (
          <div className={`ci-grid-${cols}`}>
            {channels.map(ch => <ContactChannelCard key={ch.id} ch={ch} lang={lang} cardStyle={cardStyle} cardBg={cardBg} accent={accent} cardRadius={cardRadius} onOpenContactForm={onOpenContactForm} />)}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {channels.map(ch => <ContactChannelCard key={ch.id} ch={ch} lang={lang} row cardStyle={cardStyle} cardBg={cardBg} accent={accent} cardRadius={cardRadius} onOpenContactForm={onOpenContactForm} />)}
          </div>
        )}
      </div>
    </section>
  )
}

// ── Map & Office Info ─────────────────────────────────────────────────────────
function ContactMapSection({ config, lang }) {
  const layout  = config.layout ?? 'map-left'
  const isRow   = layout === 'map-left' || layout === 'map-right'
  const reverse = layout === 'map-right'
  const address = t(config.address, lang)
  const mapHeight = Number(config.mapHeight) || 420
  const flexDirection = isRow ? (reverse ? 'row-reverse' : 'row') : (layout === 'map-bottom' ? 'column-reverse' : 'column')

  const mapEl = config.mapUrl ? (
    <iframe src={config.mapUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', border: 'none' }}
      loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Map" allowFullScreen />
  ) : (
    <div style={{ position: 'absolute', inset: 0, background: '#f1f5f9', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
      Map not configured
    </div>
  )

  return (
    <section style={{ background: config.sectionBg ?? '#ffffff', padding: `${config.sectionPadding ?? '4rem'} 2rem` }}>
      <style>{`
        .cm-wrap { display: flex; flex-direction: ${flexDirection}; gap: 2.5rem; align-items: stretch; }
        @media (max-width: 767px) { .cm-wrap { flex-direction: column; } }
      `}</style>
      <div className="container-wide mx-auto cm-wrap">
        <div style={{ flex: isRow ? 1 : undefined, position: 'relative', height: `${mapHeight}px`, borderRadius: config.mapRadius ?? '0.75rem', overflow: 'hidden' }}>{mapEl}</div>
        <div style={{ flex: isRow ? 1 : undefined }}>
          {config.showImage && config.imageUrl && (
            <img src={config.imageUrl} alt={config.imageAlt ?? ''} style={{ width: '100%', borderRadius: config.imageRadius ?? '0.75rem', marginBottom: '1rem', display: 'block' }} />
          )}
          {t(config.officeLabel, lang) && (
            <div style={{ fontSize: '0.8125rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>{t(config.officeLabel, lang)}</div>
          )}
          {t(config.officeName, lang) && (
            <h3 style={{ fontSize: config.titleSize ?? '1.5rem', fontWeight: config.titleWeight ?? '700', color: config.titleColor ?? 'var(--color-main)', marginBottom: '0.75rem' }}>{t(config.officeName, lang)}</h3>
          )}
          {address && <p style={{ fontSize: config.bodySize ?? '0.9375rem', color: config.bodyColor ?? '#6b7280', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{address}</p>}
        </div>
      </div>
    </section>
  )
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBarSection({ config, lang }) {
  const stats = config.stats ?? []
  return (
    <section style={{ background: config.sectionBg ?? 'var(--color-main)', padding: `${config.sectionPadding ?? '2.5rem'} 2rem` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'center', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        {stats.map((stat, i) => (
          <React.Fragment key={stat.id ?? i}>
            {config.dividers !== false && i > 0 && <div style={{ width: 1, height: '3rem', background: config.dividerColor ?? 'rgba(255,255,255,0.2)', flexShrink: 0 }} />}
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              {config.showIcon !== false && stat.icon && <RenderIcon icon={stat.icon} size={parseFloat(config.iconSize) * 16 || 28} color={config.valueColor ?? '#ffffff'} />}
              <span style={{ fontSize: config.valueFontSize ?? '2rem', fontWeight: config.valueFontWeight ?? '700', color: config.valueColor ?? '#ffffff', lineHeight: 1 }}>{stat.value}</span>
              <span style={{ fontSize: config.labelFontSize ?? '0.875rem', fontWeight: config.labelFontWeight ?? '400', color: config.labelColor ?? 'rgba(255,255,255,0.75)' }}>{t(stat.label, lang)}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  )
}

// ── Reviews ───────────────────────────────────────────────────────────────────
// Actual review data comes from wherever the site already sources reviews
// (Google/Facebook widget, DB, etc.) — this only lays out the header + the
// optional "leave a review" CTA, exactly as configured in the editor.
function ReviewsSection({ config, lang }) {
  const titleAlign = normalizeAlign(config.titleAlign ?? 'center')
  const ctaHelper = t(config.ctaHelperText, lang)
  const ctaLabel = t(config.ctaBtnLabel, lang) || 'Leave us a review'
  const ctaBtn = config.ctaEnabled && (
    <div style={{ textAlign: normalizeAlign(config.ctaAlign ?? 'center'), margin: '1.5rem 0' }}>
      {config.ctaShowHelper !== false && ctaHelper && <p style={{ color: '#6b7280', marginBottom: '0.75rem' }}>{ctaHelper}</p>}
      <LinkComponent href={config.ctaUrl ?? '#'} style={{
        display: 'inline-block', textDecoration: 'none', fontWeight: 700,
        background: config.ctaBg ?? 'var(--color-main)', color: config.ctaColor ?? '#ffffff',
        borderRadius: `${config.ctaRadius ?? 8}px`,
        padding: config.ctaSize === 'lg' ? '0.875rem 2rem' : config.ctaSize === 'sm' ? '0.5rem 1.25rem' : '0.7rem 1.6rem',
      }}>
        {ctaLabel}
      </LinkComponent>
    </div>
  )
  return (
    <section style={{ background: config.sectionBg ?? '#f9fafb', padding: `${config.sectionPadding ?? '4rem'} 2rem` }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {config.showTitle !== false && t(config.title, lang) && (
          <h2 style={{ textAlign: titleAlign, marginBottom: '1rem', color: config.titleColor ?? 'var(--color-main)', fontSize: config.titleSize ?? '1.75rem', fontWeight: config.titleWeight ?? '700' }}>{t(config.title, lang)}</h2>
        )}
        {config.ctaEnabled && config.ctaPosition === 'above' && ctaBtn}
        {/* Review cards render via the site's existing reviews component/data source */}
        <div id="reviews-mount" data-display-style={config.displayStyle ?? 'cards'} />
        {config.ctaEnabled && config.ctaPosition !== 'above' && ctaBtn}
      </div>
    </section>
  )
}

// ── Video Section ─────────────────────────────────────────────────────────────
function getVideoEmbedSrc(config) {
  const url = config.videoUrl ?? ''
  if (!url) return ''
  if (config.videoType === 'youtube') {
    const idMatch = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/)
    const id = idMatch ? idMatch[1] : url
    const params = new URLSearchParams()
    params.set('autoplay', config.autoplay ? '1' : '0')
    params.set('mute', config.muted !== false ? '1' : '0')
    params.set('loop', config.loop ? '1' : '0')
    params.set('controls', config.showControls !== false ? '1' : '0')
    if (config.loop) params.set('playlist', id)
    return `https://www.youtube.com/embed/${id}?${params.toString()}`
  }
  if (config.videoType === 'vimeo') {
    const idMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
    const id = idMatch ? idMatch[1] : url
    const params = new URLSearchParams()
    params.set('autoplay', config.autoplay ? '1' : '0')
    params.set('muted', config.muted !== false ? '1' : '0')
    params.set('loop', config.loop ? '1' : '0')
    params.set('controls', config.showControls !== false ? '1' : '0')
    return `https://player.vimeo.com/video/${id}?${params.toString()}`
  }
  return url
}

function VideoSectionSection({ config, lang }) {
  const displayMode = config.displayMode ?? 'block'
  const embedSrc    = getVideoEmbedSrc(config)
  const titleAlign  = normalizeAlign(config.titleAlign ?? 'center')
  const radius      = config.videoRadius ?? '0.75rem'

  if (displayMode === 'background') {
    return (
      <section style={{ position: 'relative', padding: `${config.sectionPadding ?? '4rem'} 2rem`, overflow: 'hidden', minHeight: '360px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {config.videoType === 'file' ? (
            <video src={config.videoUrl} autoPlay={config.autoplay !== false} muted={config.muted !== false} loop={config.loop !== false} playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : embedSrc ? (
            <iframe src={embedSrc} title="Background video" allow="autoplay; encrypted-media" style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : null}
          {config.overlayOpacity > 0 && <div style={{ position: 'absolute', inset: 0, background: config.overlayColor ?? '#000000', opacity: config.overlayOpacity }} />}
        </div>
        <div style={{ position: 'relative', zIndex: 1, textAlign: titleAlign, maxWidth: '700px' }}>
          {config.showTitle !== false && t(config.title, lang) && <h2 style={{ fontSize: config.titleSize ?? '1.75rem', fontWeight: 700, color: config.titleColor ?? '#ffffff', marginBottom: '0.5rem' }}>{t(config.title, lang)}</h2>}
          {t(config.subtitle, lang) && <p style={{ color: config.subtitleColor ?? '#94a3b8' }}>{t(config.subtitle, lang)}</p>}
        </div>
      </section>
    )
  }

  return (
    <section style={{ background: config.sectionBg ?? '#0f172a', padding: `${config.sectionPadding ?? '4rem'} 2rem`, textAlign: titleAlign }}>
      <div style={{ maxWidth: config.maxWidth ?? '900px', margin: '0 auto' }}>
        {config.showTitle !== false && t(config.title, lang) && <h2 style={{ fontSize: config.titleSize ?? '1.75rem', fontWeight: 700, color: config.titleColor ?? '#ffffff', marginBottom: '0.5rem' }}>{t(config.title, lang)}</h2>}
        {t(config.subtitle, lang) && <p style={{ color: config.subtitleColor ?? '#94a3b8', marginBottom: '1.5rem' }}>{t(config.subtitle, lang)}</p>}
        <div style={{ position: 'relative', aspectRatio: config.aspectRatio ?? '16/9', borderRadius: radius, overflow: 'hidden', background: '#1e293b' }}>
          {config.videoType === 'file' ? (
            <video src={config.videoUrl} controls={config.showControls !== false} autoPlay={!!config.autoplay} muted={config.muted !== false} loop={!!config.loop} playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : embedSrc ? (
            <iframe src={embedSrc} title="Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.875rem' }}>No video URL set</div>
          )}
        </div>
      </div>
    </section>
  )
}

// ── CTA / Newsletter ──────────────────────────────────────────────────────────
function CtaBannerSection({ config, lang, onOpenContactForm }) {
  const bg = config.bgType === 'gradient' ? config.bgGradient
    : config.bgType === 'image' && config.bgImageUrl ? `url(${config.bgImageUrl}) center/cover no-repeat`
    : (config.bgColor ?? 'var(--color-main)')
  const align = normalizeAlign(config.contentAlign ?? 'center')
  const isDouble = config.variant === 'double_cta'

  const renderButton = (prefix, key, defaults) => {
    const style       = config[`${prefix}Style`]       ?? defaults.style
    const color       = config[`${prefix}Color`]       ?? defaults.color
    const bgColor     = config[`${prefix}BgColor`]     ?? defaults.bg
    const borderColor = config[`${prefix}BorderColor`] ?? defaults.border
    const borderWidth = config[`${prefix}BorderWidth`] ?? 2
    const radius      = config[`${prefix}Radius`]      ?? 6
    const fontSize    = config[`${prefix}FontSize`]    ?? '1rem'
    const paddingX    = config[`${prefix}PaddingX`]    ?? '2rem'
    const paddingY    = config[`${prefix}PaddingY`]    ?? '0.75rem'
    const text        = t(config[`${prefix}Text`], lang) || defaults.text
    const url         = config[`${prefix}Url`] ?? '/'
    // Both buttons take equal width in the double-CTA layout so a short
    // label like "Call Us" doesn't end up narrower than "Book a Private
    // Viewing" — they grow/shrink together instead of hugging their text.
    const btnStyle = {
      display: 'inline-block', fontSize, fontWeight: 700, color, textAlign: 'center',
      background: style === 'ghost' ? 'transparent' : bgColor,
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius: style === 'pill' ? '999px' : `${radius}px`,
      padding: `${paddingY} ${paddingX}`, textDecoration: 'none',
      ...(isDouble ? { flex: '1 1 0', minWidth: 200 } : {}),
    }
    if (isFormTrigger(url)) {
      return <button key={key} type="button" onClick={onOpenContactForm} style={{ ...btnStyle, cursor: 'pointer', fontFamily: 'inherit' }}>{text}</button>
    }
    return (
      <LinkComponent key={key} href={url} style={btnStyle}>
        {text}
      </LinkComponent>
    )
  }

  return (
    <section style={{ background: bg, padding: `${config.sectionPadding ?? '4rem'} 2rem`, textAlign: align }}>
      {config.bgType === 'image' && config.overlayOpacity > 0 && (
        <div style={{ position: 'relative' }}><div style={{ position: 'absolute', inset: 0, background: config.overlayColor ?? '#000', opacity: config.overlayOpacity }} /></div>
      )}
      <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative' }}>
        <h2 style={{ fontSize: config.titleSize ?? '2rem', fontWeight: config.titleWeight ?? '700', color: config.titleColor ?? '#ffffff', marginBottom: '0.75rem' }}>{t(config.title, lang)}</h2>
        {t(config.subtitle, lang) && <p style={{ fontSize: config.subtitleSize ?? '1rem', color: config.subtitleColor ?? 'rgba(255,255,255,0.8)', marginBottom: '1.5rem' }}>{t(config.subtitle, lang)}</p>}
        {config.variant === 'newsletter' && (
          <form style={{ display: 'flex', gap: '0.5rem', maxWidth: '420px', margin: '0 auto', flexWrap: 'wrap', justifyContent: toJustify(align) }}>
            <input type="email" placeholder={t(config.inputPlaceholder, lang) || 'Your email address'}
              style={{ flex: 1, minWidth: '200px', padding: '0.625rem 1rem', borderRadius: `${config.inputRadius ?? 6}px`, border: 'none', fontSize: '0.9375rem' }} />
            <button type="submit" style={{ padding: '0.625rem 1.5rem', borderRadius: `${config.inputRadius ?? 6}px`, background: config.submitBg ?? '#fff', color: config.submitColor ?? 'var(--color-main)', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              {t(config.submitText, lang) || 'Subscribe'}
            </button>
          </form>
        )}
        {(config.variant === 'simple_cta' || config.variant === 'double_cta') && (
          <div style={{ display: 'flex', gap: '1rem', justifyContent: toJustify(align), flexWrap: 'wrap' }}>
            {renderButton('cta', 'p', { style: 'outlined', color: '#ffffff', bg: 'transparent', border: '#ffffff', text: 'Browse Listings' })}
            {isDouble && renderButton('cta2', 's', { style: 'filled', color: 'var(--color-main)', bg: '#ffffff', border: '#ffffff', text: 'List Your Property' })}
          </div>
        )}
      </div>
    </section>
  )
}

// ── Contact Buttons Block ─────────────────────────────────────────────────────
function ContactButtonsBlock({ block, lang }) {
  const buttons = block.buttons ?? []
  const layout  = block.layout  ?? 'row'
  const align   = normalizeAlign(block.align ?? 'center')
  const gap     = block.gap     ?? '0.75rem'
  const btnSize = block.btnSize ?? 'md'

  const sizeMap = {
    sm: { padding: '0.5rem 1rem',    fontSize: '0.8125rem', iconSize: 16 },
    md: { padding: '0.75rem 1.5rem', fontSize: '0.9375rem', iconSize: 18 },
    lg: { padding: '1rem 2rem',      fontSize: '1.0625rem', iconSize: 20 },
  }
  const sz = sizeMap[btnSize]

  const containerStyle = layout === 'column'
    ? { display: 'flex', flexDirection: 'column', gap, alignItems: toAlignItems(align) }
    : layout === 'grid'
      ? { display: 'grid', gridTemplateColumns: `repeat(${block.gridCols ?? 2}, minmax(0, 1fr))`, gap }
      : { display: 'flex', flexWrap: 'wrap', gap, justifyContent: toJustify(align) }

  return (
    <div style={{
      background:   block.noBg ? 'transparent' : (block.bgColor ?? '#ffffff'),
      borderRadius: block.borderRadius ?? '0.75rem',
      padding:      block.padding ?? '1.5rem',
      boxShadow:    block.shadow ? '0 4px 24px rgba(0,0,0,0.08)' : 'none',
      height: '100%', boxSizing: 'border-box',
    }}>
      <div style={containerStyle}>
        {buttons.map((btn, i) => {
          const label   = t(btn.label, lang) || ''
          const href    = btn.url || '#'
          const bgColor = btn.bgColor   ?? '#374151'
          const txColor = btn.textColor ?? '#ffffff'
          const radius  = btn.style === 'pill' ? 9999 : 8
          return (
            <a key={btn.id ?? i} href={href}
              target={btn.openNewTab !== false ? '_blank' : undefined}
              rel={btn.openNewTab !== false ? 'noopener noreferrer' : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: sz.padding, fontSize: sz.fontSize, fontWeight: 600,
                borderRadius: radius, textDecoration: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                border:      btn.style === 'outlined' ? `2px solid ${bgColor}` : 'none',
                background:  btn.style === 'outlined' ? 'transparent' : bgColor,
                color:       btn.style === 'outlined' ? bgColor : txColor,
              }}>
              <ContactIcon preset={btn.preset} size={sz.iconSize} color={btn.style === 'outlined' ? bgColor : txColor} />
              {block.showLabel !== false && label && <span>{label}</span>}
            </a>
          )
        })}
      </div>
    </div>
  )
}

// ── Map Embed Block ───────────────────────────────────────────────────────────
function MapEmbedBlock({ block }) {
  if (!block.embedUrl) return null
  return (
    <div style={{ width: block.width ?? '100%', position: 'relative', borderRadius: block.borderRadius ?? '0.5rem', overflow: 'hidden', aspectRatio: block.aspectRatio ?? '16/9', background: '#e2e8f0' }}>
      <iframe src={block.embedUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', border: 'none' }}
        loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Map" allowFullScreen />
    </div>
  )
}

// ── Facebook Timeline Block ───────────────────────────────────────────────────
function FacebookTimelineBlock({ block }) {
  const pageUrl = block.fbPageUrl ?? ''
  const w  = block.fbWidth        ?? 500
  const h  = block.fbHeight       ?? 470
  const wm = block.fbWidthMobile  ?? 380
  const hm = block.fbHeightMobile ?? 400
  const buildSrc = (width, height) =>
    `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(pageUrl)}&tabs=timeline&width=${width}&height=${height}&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`
  return (
    <div style={{ borderRadius: block.borderRadius ?? '0.75rem', overflow: 'hidden', lineHeight: 0 }}>
      <Script async defer crossOrigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v19.0" />
      <div className="sm:block hidden">
        <iframe title="Facebook Feed" src={buildSrc(w, h)} width={w} height={h} scrolling="no" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" style={{ display: 'block', border: 'none' }} />
      </div>
      <div className="block sm:hidden">
        <iframe title="Facebook Feed" src={buildSrc(wm, hm)} width={wm} height={hm} scrolling="no" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" style={{ display: 'block', border: 'none' }} />
      </div>
    </div>
  )
}

// ── Video Embed Block (youtube / tiktok) ──────────────────────────────────────
function VideoEmbedBlock({ block }) {
  const isTT = (block.videoType ?? 'youtube') === 'tiktok'
  let src = ''
  if (!isTT) {
    const idMatch = (block.videoUrl ?? '').match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/)
    const id = idMatch ? idMatch[1] : block.videoUrl
    src = id ? `https://www.youtube.com/embed/${id}` : ''
  }
  return (
    <div style={{ background: '#0f172a', borderRadius: block.borderRadius ?? '0.75rem', overflow: 'hidden', height: '100%' }}>
      {isTT ? (
        block.videoUrl ? (
          <blockquote className="tiktok-embed" cite={block.videoUrl} style={{ margin: 0 }}>
            <a href={block.videoUrl} target="_blank" rel="noopener noreferrer">{block.videoUrl}</a>
          </blockquote>
        ) : null
      ) : src ? (
        <div style={{ position: 'relative', aspectRatio: block.videoAspect ?? '16/9' }}>
          <iframe src={src} title="Video" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
        </div>
      ) : null}
      {isTT && <Script async src="https://www.tiktok.com/embed.js" />}
    </div>
  )
}

// ── Feature Showcase Block ────────────────────────────────────────────────────
function FeatureShowcaseBlock({ block, lang }) {
  const img   = (block.showcaseImages ?? [])[0]
  const isRow = (block.showcaseLayout ?? 'image-left') !== 'stacked'
  const isRight = block.showcaseLayout === 'image-right'
  return (
    <div style={{ background: block.showcaseBg ?? '#ffffff', borderRadius: block.borderRadius ?? '0.75rem', border: '1px solid #e5e7eb', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: isRow ? (isRight ? 'row-reverse' : 'row') : 'column' }}>
      <div style={{ flexShrink: 0, width: isRow ? '45%' : '100%', position: 'relative', minHeight: isRow ? undefined : 200, aspectRatio: isRow ? undefined : '16/9', background: '#e2e8f0' }}>
        {img && <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      </div>
      <div style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {t(block.showcaseEyebrow, lang) && <div style={{ fontSize: '0.75rem', fontWeight: 700, color: block.showcaseAccentColor ?? 'var(--color-main)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t(block.showcaseEyebrow, lang)}</div>}
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{t(block.showcaseTitle, lang) || 'Project Name'}</div>
        {t(block.showcaseLocation, lang) && <div style={{ color: '#64748b' }}>📍 {t(block.showcaseLocation, lang)}</div>}
        {t(block.showcasePrice, lang) && <div style={{ fontWeight: 700, color: block.showcaseAccentColor ?? 'var(--color-main)' }}>From {t(block.showcasePrice, lang)}</div>}
        {t(block.showcaseQuote, lang) && <p style={{ fontStyle: 'italic', color: '#475569' }}>&ldquo;{t(block.showcaseQuote, lang)}&rdquo;</p>}
        {(block.showcaseStats ?? []).length > 0 && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            {block.showcaseStats.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: '#475569', fontWeight: 600 }}>
                <RenderIcon icon={s.icon} size={14} color="#475569" />
                {t(s.value, lang)} <span style={{ fontWeight: 400 }}>{t(s.label, lang)}</span>
              </div>
            ))}
          </div>
        )}
        {t(block.showcaseCtaText, lang) && (
          <LinkComponent href={block.showcaseCtaUrl || '/'} style={{
            marginTop: '0.5rem', alignSelf: 'flex-start', textDecoration: 'none', fontWeight: 700,
            padding: '0.5rem 1.25rem', borderRadius: 6,
            background: block.showcaseCtaStyle === 'filled' ? (block.showcaseCtaColor ?? 'var(--color-main)') : 'transparent',
            color: block.showcaseCtaStyle === 'filled' ? '#fff' : (block.showcaseCtaColor ?? 'var(--color-main)'),
            border: block.showcaseCtaStyle === 'outlined' ? `1.5px solid ${block.showcaseCtaColor ?? 'var(--color-main)'}` : 'none',
          }}>
            {t(block.showcaseCtaText, lang)}
          </LinkComponent>
        )}
        {(block.showcaseShowWhatsapp || block.showcaseShowLine) && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            {block.showcaseShowWhatsapp && block.showcaseWhatsappNumber && (
              <a href={`https://wa.me/${block.showcaseWhatsappNumber}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 600, color: '#25d366' }}>WhatsApp</a>
            )}
            {block.showcaseShowLine && block.showcaseLineId && (
              <a href={`https://line.me/ti/p/~${block.showcaseLineId.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 600, color: '#06c755' }}>LINE</a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Image Grid Block ──────────────────────────────────────────────────────────
function ImageGridBlock({ block }) {
  const layout = GRID_LAYOUTS[block.gridLayout ?? 'feature_left'] ?? GRID_LAYOUTS.feature_left
  const slots  = getGridSlotLetters(layout.areas)
  const images = block.images ?? []
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: layout.cols, gridTemplateRows: layout.rows, gridTemplateAreas: layout.areas,
      gap: `${block.imageGap ?? 12}px`, height: `${block.gridHeight ?? 420}px`,
      borderRadius: block.borderRadius ?? '0.75rem', overflow: block.shadow ? 'visible' : 'hidden',
    }}>
      {slots.map((letter, i) => (
        <div key={letter} style={{ gridArea: letter, position: 'relative', borderRadius: `${block.imageGridRadius ?? 10}px`, overflow: 'hidden', background: '#e2e8f0', boxShadow: block.shadow ? '0 4px 16px rgba(0,0,0,0.1)' : 'none' }}>
          {images[i] && <img src={images[i]} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
      ))}
    </div>
  )
}

// ── Content Block (single cell) ───────────────────────────────────────────────
function ContentBlock({ block, lang, websiteData, onOpenContactForm }) {
  if (block.type === 'contact_buttons')   return <ContactButtonsBlock block={block} lang={lang} />
  if (block.type === 'map_embed')         return <MapEmbedBlock block={block} />
  if (block.type === 'facebook_timeline') return <FacebookTimelineBlock block={block} />
  if (block.type === 'video_embed')       return <VideoEmbedBlock block={block} />
  if (block.type === 'feature_showcase')  return <FeatureShowcaseBlock block={block} lang={lang} />
  if (block.type === 'image_grid')        return <ImageGridBlock block={block} />
  if (block.type === 'form_sell')         return <SellFormRenderer        cardStyle={websiteData?.sellForm}        lang={lang} userId={websiteData?.userId} />
  if (block.type === 'form_contact')      return <ContactFormRenderer     cardStyle={websiteData?.contactForm}     lang={lang} userId={websiteData?.userId} />
  if (block.type === 'form_recruitment')  return <RecruitmentFormRenderer cardStyle={websiteData?.recruitmentForm} lang={lang} userId={websiteData?.userId} />

  const align         = normalizeAlign(block.textAlign ?? 'left')
  const isImageLeft   = block.type === 'image+text' && block.imagePosition === 'left'
  const isImageRight  = block.type === 'image+text' && block.imagePosition === 'right'
  const isIconBeside  = block.type === 'icon+text' && block.iconLayout === 'left'
  const isRow         = isImageLeft || isImageRight || isIconBeside

  const titleColor = block.titleColor ?? 'var(--color-main)'
  const bodyColor  = block.bodyColor  ?? '#6b7280'
  const ctaColor   = block.ctaColor   ?? 'var(--color-main)'
  const bodyHtml   = t(block.body, lang)

  const imageEl = (block.type === 'image+text' || block.type === 'image_only') && block.imageUrl ? (
    <div style={{ position: 'relative', aspectRatio: block.imageAspect === 'auto' ? undefined : (block.imageAspect ?? '16/9'), borderRadius: block.imageRadius ?? '0.5rem', overflow: 'hidden', flexShrink: isRow ? 0 : undefined, width: isRow ? '42%' : '100%' }}>
      {block.imageWidth && block.imageHeight ? (
        <Image src={block.imageUrl} alt={block.imageAlt ?? ''} width={block.imageWidth} height={block.imageHeight} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <img src={block.imageUrl} alt={block.imageAlt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
    </div>
  ) : null

  const iconGlyph = <RenderIcon icon={block.icon} size={(parseFloat(block.iconSize) || 2) * 16} color={block.iconColor ?? 'var(--color-main)'} />
  const iconEl = block.type === 'icon+text' ? (
    <div style={{ textAlign: align, lineHeight: 1, flexShrink: 0 }}>
      {block.iconContainer ? (
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: block.iconBg ?? 'var(--color-main)',
          border: `${block.iconBorderWidth ?? 0}px solid ${block.iconBorderColor ?? 'transparent'}`,
          borderRadius: `${block.iconRadius ?? 999}px`, padding: `${block.iconPadding ?? 14}px`,
        }}>{iconGlyph}</div>
      ) : iconGlyph}
    </div>
  ) : null

  const ctaEl = block.showCta && t(block.ctaText, lang) ? (
    isFormTrigger(block.ctaUrl) ? (
      <button type="button" onClick={onOpenContactForm}
        style={{ display: 'inline-block', marginTop: '1rem', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', color: block.ctaStyle === 'filled' ? '#ffffff' : ctaColor, textDecoration: block.ctaStyle === 'text' ? 'underline' : 'none', padding: (block.ctaStyle === 'filled' || block.ctaStyle === 'outlined') ? '0.5rem 1.25rem' : 0, background: block.ctaStyle === 'filled' ? ctaColor : 'transparent', border: block.ctaStyle === 'outlined' ? `1.5px solid ${ctaColor}` : 'none', borderRadius: 5 }}>
        {t(block.ctaText, lang)}
      </button>
    ) : (
      <LinkComponent href={block.ctaUrl ?? '/'}
        style={{ display: 'inline-block', marginTop: '1rem', fontWeight: 600, color: block.ctaStyle === 'filled' ? '#ffffff' : ctaColor, textDecoration: block.ctaStyle === 'text' ? 'underline' : 'none', padding: (block.ctaStyle === 'filled' || block.ctaStyle === 'outlined') ? '0.5rem 1.25rem' : 0, background: block.ctaStyle === 'filled' ? ctaColor : 'transparent', border: block.ctaStyle === 'outlined' ? `1.5px solid ${ctaColor}` : 'none', borderRadius: 5 }}>
        {t(block.ctaText, lang)}
      </LinkComponent>
    )
  ) : null

  const textEl = block.type !== 'image_only' ? (
    <div style={{ textAlign: align, flex: isRow ? 1 : undefined, minWidth: 0 }}>
      {t(block.title, lang) && (
        <h3 style={{ fontSize: block.titleSize ?? '1.25rem', fontWeight: block.titleWeight ?? '600', color: titleColor, marginBottom: '0.5rem', marginTop: (iconEl && !isRow) ? '0.75rem' : 0, lineHeight: 1.3 }}>
          {t(block.title, lang)}
        </h3>
      )}
      {bodyHtml && (
        <div className="cb-prose" style={{ fontSize: block.bodySize ?? '0.9375rem', color: bodyColor, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
      )}
      {ctaEl}
    </div>
  ) : null

  return (
    <div style={{
      background:    block.noBg ? 'transparent' : (block.bgColor ?? '#ffffff'),
      borderRadius:  block.borderRadius ?? '0.75rem',
      padding:       block.padding ?? '1.5rem',
      boxShadow:     block.shadow ? '0 4px 24px rgba(0,0,0,0.08)' : 'none',
      border:        (!block.noBorder && block.borderColor) ? `1px solid ${block.borderColor}` : 'none',
      display:       'flex',
      flexDirection: isRow ? 'row' : 'column',
      gap:           '1rem',
      alignItems:    isRow ? (block.iconVAlign ? { top: 'flex-start', center: 'center', bottom: 'flex-end' }[block.iconVAlign] ?? 'center' : 'center') : toAlignItems(align),
      height:        '100%',
      boxSizing:     'border-box',
    }}>
      {isImageLeft  && imageEl}
      {!isRow && (block.type === 'image+text' || block.type === 'image_only') && imageEl}
      {iconEl}
      {textEl}
      {isImageRight && imageEl}
    </div>
  )
}

// ── Content Blocks Section ────────────────────────────────────────────────────
function ContentBlocksSection({ config, websiteData, lang, onOpenContactForm, sectionId }) {
  const blocks     = config.blocks ?? []
  // Effective column count is capped at the number of blocks that actually
  // exist — otherwise a section configured for e.g. 2 columns with only one
  // block renders that block at 50% width with a permanently empty second
  // grid cell beside it, instead of the block spanning full width.
  const cols       = Math.max(1, Math.min(config.columns ?? 2, blocks.length || 1))
  const alignItems = config.verticalAlign === 'stretch' ? 'stretch' : config.verticalAlign === 'center' ? 'center' : 'start'
  const titleAlign = normalizeAlign(config.titleAlign ?? 'left')

  const bg = config.bgType === 'gradient' ? config.bgGradient
    : config.bgType === 'image' && config.bgImageUrl ? `url(${config.bgImageUrl}) center/cover no-repeat`
    : (config.sectionBg ?? '#ffffff')

  // Scoped per section instance — a shared ".cb-grid" class means that if a
  // page ever has more than one content_blocks section, each section's
  // <style> tag defines the same global selector, and the LAST one in the
  // document silently overrides every other section's column count.
  const gridClass = `cb-grid-${String(sectionId ?? 'x').replace(/[^a-zA-Z0-9_-]/g, '')}`

  return (
    <section style={{ position: 'relative', background: bg, padding: `${config.sectionPadding ?? '4rem'} 0` }}>
      <style>{`
        .${gridClass} { display: grid; grid-template-columns: 1fr; gap: ${config.gap ?? '2rem'}; align-items: ${alignItems}; }
        @media (min-width: 640px)  { .${gridClass} { grid-template-columns: repeat(${Math.min(cols, 2)}, 1fr); } }
        @media (min-width: 1024px) { .${gridClass} { grid-template-columns: repeat(${cols}, 1fr); } }
      `}</style>
      {config.bgType === 'image' && config.overlayOpacity > 0 && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: config.overlayColor ?? '#000000', opacity: config.overlayOpacity }} />
      )}
      <div className="container-wide mx-auto" style={{ position: 'relative' }}>
        {(config.showTitle || config.showSubtitle) && (
          <div style={{ marginBottom: '2rem', textAlign: titleAlign }}>
            {config.showTitle && t(config.title, lang) && (
              <h2 style={{ fontSize: config.titleSize ?? '1.75rem', fontWeight: config.titleWeight ?? '700', color: config.titleColor ?? 'var(--color-main)', margin: 0 }}>{t(config.title, lang)}</h2>
            )}
            {config.showSubtitle && t(config.subtitle, lang) && (
              <p style={{ fontSize: config.subtitleSize ?? '1rem', color: config.subtitleColor ?? '#6b7280', marginTop: '0.5rem' }}>{t(config.subtitle, lang)}</p>
            )}
            {config.showViewAll && t(config.viewAllText, lang) && (
              <LinkComponent href={config.viewAllUrl ?? '/'} style={{ fontWeight: 700, color: 'var(--color-main)', textDecoration: 'none', display: 'inline-block', marginTop: '0.5rem' }}>
                {t(config.viewAllText, lang)} →
              </LinkComponent>
            )}
          </div>
        )}
        <div className={gridClass}>
          {blocks.map((block, i) => <ContentBlock key={block.id ?? i} block={block} websiteData={websiteData} lang={lang} onOpenContactForm={onOpenContactForm} />)}
        </div>
      </div>
    </section>
  )
}

// ── Card Carousel ─────────────────────────────────────────────────────────────
function CarouselCard({ card, config, lang, gridRow, flexFill }) {
  const variant    = config.cardVariant ?? 'default'
  const title      = t(card.title, lang)
  const desc       = t(card.description, lang)
  const ctaText    = t(card.ctaText, lang)
  const titleColor = card.overrideStyle ? (card.titleColor ?? '#0f172a') : (config.cardTitleColor ?? '#0f172a')
  const descColor  = card.overrideStyle ? (card.descColor  ?? '#64748b') : (config.cardDescColor  ?? '#64748b')
  const cardBg     = card.overrideStyle ? (card.cardBg     ?? '#f8fafc') : (config.cardBg ?? '#f8fafc')
  const cardBorder = card.overrideStyle ? (card.cardBorder ?? '#e2e8f0') : (config.cardBorder ?? '#e2e8f0')
  const badgeBg    = card.overrideStyle ? (card.badgeBg    ?? '#eff6ff') : (config.badgeBg ?? '#eff6ff')
  const badgeColor = card.overrideStyle ? (card.badgeColor ?? '#1d4ed8') : (config.badgeColor ?? '#1d4ed8')
  const showImage  = variant !== 'minimal'

  const clamp = (n) => n ? { display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}

  const titleEl = title && (
    <div style={{ fontSize: config.cardTitleSize ?? '1rem', fontWeight: config.cardTitleWeight ?? '600', color: variant === 'overlay' ? '#ffffff' : titleColor, textAlign: normalizeAlign(config.cardTitleAlign ?? 'left'), ...clamp(config.cardTitleClamp) }}>{title}</div>
  )
  const descEl = desc && (
    <div style={{ fontSize: config.cardDescSize ?? '0.875rem', fontWeight: config.cardDescWeight ?? '400', color: variant === 'overlay' ? 'rgba(255,255,255,0.85)' : descColor, textAlign: normalizeAlign(config.cardDescAlign ?? 'left'), lineHeight: 1.5, ...clamp(config.cardDescClamp) }}>{desc}</div>
  )
  const badgeEl = card.badge && (
    <span style={{ fontSize: config.badgeFontSize ?? '0.6875rem', fontWeight: 700, padding: '0.25rem 0.75rem', borderRadius: config.badgeRadius ?? 20, background: badgeBg, color: badgeColor, width: 'fit-content' }}>{card.badge}</span>
  )
  const ctaEl = config.ctaShow !== false && ctaText && (
    <span style={{
      marginTop: '0.5rem', display: config.ctaFullWidth ? 'block' : 'inline-block', width: config.ctaFullWidth ? '100%' : 'auto',
      textAlign: normalizeAlign(config.ctaAlign ?? 'left'), fontWeight: 700, fontSize: config.ctaFontSize ?? '0.8125rem',
      color: config.ctaStyle === 'filled' ? (config.ctaColor ?? '#fff') : (config.ctaBg ?? 'var(--color-main)'),
      background: config.ctaStyle === 'filled' ? (config.ctaBg ?? 'var(--color-main)') : 'transparent',
      border: config.ctaStyle === 'outlined' ? `1.5px solid ${config.ctaBg ?? 'var(--color-main)'}` : 'none',
      borderRadius: config.ctaRadius ?? 6,
      padding: (config.ctaStyle === 'filled' || config.ctaStyle === 'outlined') ? `${config.ctaPaddingY ?? '6px'} ${config.ctaPaddingX ?? '16px'}` : 0,
      textDecoration: config.ctaStyle === 'text' ? 'underline' : 'none', boxSizing: 'border-box',
    }}>{ctaText}</span>
  )

  const imageEl = showImage && (
    <div style={{ position: 'relative', width: variant === 'horizontal' ? '40%' : '100%', flexShrink: 0, aspectRatio: config.imageAspect ?? '1/1', background: '#e2e8f0', overflow: 'hidden' }}>
      {card.imageUrl && <img src={card.imageUrl} alt={title || ''} style={{ width: '100%', height: '100%', objectFit: config.imageFit ?? 'cover', display: 'block' }} />}
      {variant === 'overlay' && <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, ${config.overlayStart ?? 'rgba(0,0,0,0)'}, ${config.overlayEnd ?? 'rgba(0,0,0,0.7)'})` }} />}
      {badgeEl && <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem' }}>{badgeEl}</div>}
      {variant === 'overlay' && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: `${config.cardPaddingY ?? 12}px ${config.cardPaddingX ?? 14}px`, display: 'flex', flexDirection: 'column', gap: config.cardContentGap ?? 4 }}>
          {titleEl}{descEl}{ctaEl}
        </div>
      )}
    </div>
  )

  const bodyEl = variant !== 'overlay' && (
    <div style={{ padding: `${config.cardPaddingY ?? 12}px ${config.cardPaddingX ?? 14}px`, display: 'flex', flexDirection: 'column', gap: config.cardContentGap ?? 4, flex: 1 }}>
      {!showImage && badgeEl}
      {card.icon && <div style={{ textAlign: normalizeAlign(config.cardIconAlign ?? 'left') }}><RenderIcon icon={card.icon} size={parseFloat(config.cardIconSize) * 16 || 32} /></div>}
      {titleEl}{descEl}{ctaEl}
    </div>
  )

  const visualStyle = {
    background:   variant === 'overlay' ? '#0f172a' : cardBg,
    border:       `${config.cardBorderWidth ?? 1}px solid ${cardBorder}`,
    borderRadius: config.cardRadius ?? 12,
    overflow:     'hidden',
    boxShadow:    config.cardShadow !== false ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
    display:      'flex',
    flexDirection: variant === 'horizontal' ? 'row' : 'column',
    height:       '100%',
    position:     'relative',
  }
  const outerStyle = { gridRow, flex: flexFill ? 1 : undefined, height: '100%', textDecoration: 'none', color: 'inherit' }
  const content = <div style={visualStyle}>{imageEl}{bodyEl}</div>
  return card.url ? <a href={card.url} style={outerStyle}>{content}</a> : <div style={outerStyle}>{content}</div>
}

function CarouselEntry({ entry, config, lang, displayMode }) {
  if (entry.type === 'stack') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: `${config.cardGap ?? 20}px`, gridRow: displayMode === 'grid' ? 'span 2' : undefined, height: '100%' }}>
        {entry.cards.map(card => <CarouselCard key={card.id} card={card} config={config} lang={lang} flexFill />)}
      </div>
    )
  }
  const card = entry.cards[0]
  const rowSpan = card.rowSpan === 2 ? 2 : 1
  return <CarouselCard card={card} config={config} lang={lang} gridRow={displayMode === 'grid' ? `span ${rowSpan}` : undefined} />
}

function CardCarouselSection({ config, lang }) {
  const entries = config.entries ?? []
  if (!entries.length) return null

  const displayMode = config.displayMode ?? 'carousel'
  const rowHeight    = config.gridRowHeight ?? 220
  const gap          = config.cardGap ?? 20
  const titleAlign   = normalizeAlign(config.titleAlign ?? 'left')

  const layoutCss = displayMode === 'grid' ? `
    .cc-grid {
      display: grid; grid-template-columns: repeat(${config.gridColsMobile ?? 1}, 1fr);
      grid-auto-rows: ${rowHeight}px; gap: ${gap}px;
    }
    @media (min-width: 640px)  { .cc-grid { grid-template-columns: repeat(${config.gridColsTablet ?? 2}, 1fr); } }
    @media (min-width: 1024px) { .cc-grid { grid-template-columns: repeat(${config.gridCols ?? 3}, 1fr); } }
  ` : `
    .cc-scroll { display: flex; gap: ${gap}px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 0.5rem; }
    .cc-scroll::-webkit-scrollbar { height: 6px; }
    .cc-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
    .cc-scroll > * { scroll-snap-align: start; flex-shrink: 0; width: ${100 / (config.cardsMobile ?? 1.2)}%; }
    @media (min-width: 640px)  { .cc-scroll > * { width: ${100 / (config.cardsTablet ?? 2)}%; } }
    @media (min-width: 1024px) { .cc-scroll > * { width: ${100 / (config.cardsDesktop ?? 3)}%; } }
  `

  return (
    <section style={{ background: config.sectionBg ?? '#ffffff', padding: `${config.sectionPy ?? '3rem'} ${config.sectionPx ?? '2rem'}` }}>
      <div className="container-wide mx-auto">
        {(config.showTitle !== false || config.showDesc || config.showViewAll) && (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <div>
              {config.showTitle !== false && t(config.title, lang) && (
                <h2 style={{ fontSize: config.titleSize ?? '1.75rem', fontWeight: config.titleWeight ?? '700', color: config.titleColor ?? '#0f172a', textAlign: titleAlign, margin: 0 }}>{t(config.title, lang)}</h2>
              )}
              {config.showDesc && t(config.desc, lang) && (
                <p style={{ fontSize: config.descSize ?? '1rem', color: config.descColor ?? '#64748b', marginTop: '0.5rem' }}>{t(config.desc, lang)}</p>
              )}
            </div>
            {config.showViewAll && t(config.viewAllText, lang) && (
              <LinkComponent href={config.viewAllUrl ?? '/'} style={{ fontWeight: 700, color: 'var(--color-main)', textDecoration: 'none', whiteSpace: 'nowrap' }}>{t(config.viewAllText, lang)} →</LinkComponent>
            )}
          </div>
        )}
        <style>{layoutCss}</style>
        <div className={displayMode === 'grid' ? 'cc-grid' : 'cc-scroll'}>
          {entries.map(entry => <CarouselEntry key={entry.id} entry={entry} config={config} lang={lang} displayMode={displayMode} />)}
        </div>
      </div>
    </section>
  )
}

// ── Divider ───────────────────────────────────────────────────────────────────
function DividerSection({ config }) {
  const mt    = Number(config.marginTop    ?? 0)
  const mb    = Number(config.marginBottom ?? 0)
  const style = config.style ?? 'solid'
  const color = config.color ?? '#e5e7eb'
  const thick = Number(config.thickness ?? 1)
  const w     = Number(config.width ?? 100)
  const justify = toJustify(normalizeAlign(config.align ?? 'center'))

  if (style === 'none') return <div style={{ marginTop: mt, marginBottom: mb }} />

  return (
    <div style={{ marginTop: mt, marginBottom: mb, display: 'flex', justifyContent: justify }}>
      <div style={{
        width: `${w}%`, height: style === 'gradient' ? thick : 0,
        background: style === 'gradient' ? `linear-gradient(to right, transparent, ${color}, transparent)` : 'none',
        borderTop: style !== 'gradient' ? `${thick}px ${style} ${color}` : 'none',
      }} />
    </div>
  )
}

// ── Section router ────────────────────────────────────────────────────────────
function renderSection({ section, idx, lang, website, onOpenContactForm }) {
  const config = section.config ?? {}
  const key    = section.id ?? idx

  switch (section.type) {
    case 'banner':          return <BannerSection key={key} config={config} lang={lang} onOpenContactForm={onOpenContactForm} />
    case 'contact_info':    return <ContactInfoSection key={key} config={config} lang={lang} onOpenContactForm={onOpenContactForm} />
    case 'contact_map':     return <ContactMapSection key={key} config={config} lang={lang} />
    case 'content_blocks':  return <ContentBlocksSection key={key} config={config} lang={lang} websiteData={website} onOpenContactForm={onOpenContactForm} sectionId={key} />
    case 'card_carousel':   return <CardCarouselSection key={key} config={config} lang={lang} />
    case 'stats_bar':       return <StatsBarSection key={key} config={config} lang={lang} />
    case 'reviews':         return <ReviewsSection key={key} config={config} lang={lang} />
    case 'video_section':   return <VideoSectionSection key={key} config={config} lang={lang} />
    case 'cta_banner':      return <CtaBannerSection key={key} config={config} lang={lang} onOpenContactForm={onOpenContactForm} />
    case 'divider':         return <DividerSection key={key} config={config} />
    default:                return null
  }
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function ContactPageRenderer({ sections = [], website, lang }) {
  const [contactFormOpen, setContactFormOpen] = useState(false)
  if (!sections.length) return null
  return (
    <>
      {sections.map((section, idx) => renderSection({ section, idx, lang, website, onOpenContactForm: () => setContactFormOpen(true) }))}
      {contactFormOpen && <ContactFormModal onClose={() => setContactFormOpen(false)} website={website} lang={lang} />}
    </>
  )
}